import React, { useState } from 'react';
import {
  useGetUsersListWithRolesQuery,
  useLazyGetUserWithRolesQuery,
} from '@app/store/api/qm-module/qm-users/qm-users.api';
import { CircleButton, EasyModal, Input } from '@ui';
import useTranslation from '@app/hooks/use-translation';
import { TableColumn } from '@app/components/rubber-table/table.types';
import { PaginationResponse } from '@app/components/ui/paginator/paginator';
import { QmUsersListRequestType, QmUserWithRoleType } from '@app/interfaces/qm-users.type';
import { QmUsersTableBlock } from '@app/pages/users/blocks/qm-users-table.block';
import QmUsersForm from '@app/pages/users/blocks/qm-users-form';
import useDebounce from '@app/hooks/use-debounce';

const QmUsersPage = () => {
  const { t } = useTranslation('pages.usersPage.qm');
  const [userFilterData, setUserFilterData] = useState<string>();
  const debouncedValue = useDebounce(userFilterData || '');
  const [usersBulkRequestData, setUsersBulkRequestData] = useState<
    Omit<QmUsersListRequestType, 'offset' | 'limit'>
  >({
    sortDesc: false,
    with_deleted: true,
  });
  const [editableUserData, setEditableUserData] = useState<QmUserWithRoleType>();
  const [currentPaginationData, setPaginationData] = useState<PaginationResponse>({
    limit: 10,
    offset: 0,
  });

  // api
  const [getQmUserInfo] = useLazyGetUserWithRolesQuery();
  const { data: qmUsersList } = useGetUsersListWithRolesQuery({
    ...usersBulkRequestData,
    ...currentPaginationData,
    filter: (encodeURI(debouncedValue).split(' ') as unknown as string) || undefined,
  });
  // api

  function handleEditQmUser(id: string) {
    getQmUserInfo({ id })
      .unwrap()
      .then((res) => setEditableUserData(res));
  }

  function handleCloseModal() {
    setEditableUserData(undefined);
  }

  const tableData = qmUsersList?.qm_users.map((item) => ({
    ...item,
    role: item.qm.role ? t(`roles.${item.qm.role}`) : '-',
    operator_id: item.qm.operator_id ?? '-',
    deleted: (
      <div className="flex w-full items-center justify-center">
        <div
          className={`${!item.deleted ? 'bg-basic_green' : 'bg-basic_red'} rounded-full p-2 w-fit`}
        />
      </div>
    ),
    actions: (
      <div className="w-full flex items-center justify-center ">
        <CircleButton
          hintTitle={t('table.edit')}
          icon="SettingIcon"
          size={18}
          onClick={() => handleEditQmUser(item.id)}
          disable={item.deleted}
        />
      </div>
    ),
  }));

  const tableColumns: TableColumn[] = [
    { title: t('table.username'), index: 'username', filter: true },
    { title: t('table.role'), index: 'role' },
    { title: t('table.operator_id'), index: 'operator_id' },
    { title: t('table.deleted'), index: 'deleted', maxWidth: 100 },
    { title: t('table.actions'), index: 'actions', maxWidth: 110 },
  ];

  const handleChangePage = ({ offset, limit }: PaginationResponse) => {
    setPaginationData({ offset, limit });
  };

  return (
    <div className="gap-[20px] flex flex-col">
      <div className="flex items-center pt-[30px] pb-[10px] gap-[10px]">
        <div className="w-[300px]">
          <Input
            defaultValue={usersBulkRequestData.filter}
            onChange={(event) => setUserFilterData(event.target.value)}
            icon="SearchIcon"
            placeholder={t('user_search_placeholder')}
          />
        </div>
      </div>
      <QmUsersTableBlock
        currentPaginationData={currentPaginationData}
        handleChangePage={handleChangePage}
        total={qmUsersList?.total || 0}
        onFilter={(filter) => {
          const sort = {
            sortBy: Object.keys(filter)[0],
            sortDesc: Object.keys(filter)[0] ? 0 < Object.values(filter)[0] : undefined,
          };
          setUsersBulkRequestData((prev) => ({ ...prev, ...sort }));
        }}
        tableData={tableData}
        tableColumns={tableColumns}
      />

      <EasyModal
        label={t('form.label')}
        withoutFooter
        variant={'mediumW650'}
        show={!!editableUserData}
        onClose={handleCloseModal}
      >
        {editableUserData && <QmUsersForm onClose={handleCloseModal} userData={editableUserData} />}
      </EasyModal>
    </div>
  );
};

export default QmUsersPage;
